import { map } from 'rxjs';
import { DatePipe } from '@angular/common';
import { FormControl, FormGroup } from '@angular/forms';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';

import { SelectItem } from 'primeng/api';
import { TableLazyLoadEvent } from 'primeng/table';
import { TranslateService } from '@ngx-translate/core';
import { MultiSelectSelectAllChangeEvent } from 'primeng/multiselect';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

import { common, Hub } from '@qwyk/models';
import { LocalStorageService } from '@qwyk/core';
import { MD5Pipe, UrlEncodePipe } from '@qwyk/ui-standalone';
import { AuthenticationFacade } from '@qwyk/hub/authentication';
import { PortalTeamsFacade } from '@qwyk/hub/administration/data-access/portal-teams';
import { CustomerUsersFacade } from '@qwyk/hub/administration/data-access/customer-users';

import { CustomerUserImpersonateModalComponent } from '../customer-user-impersonate-modal/customer-user-impersonate-modal.component';

export enum CustomerUserColumn {
    EMAIL = 'email',
    NAME = 'name',
    TEAM_NAME = 'team_name',
    CREATED_AT = 'created_at',
    DEACTIVATED = 'deactivated',
    DEACTIVATED_BY = 'deactivated_by',
}

@Component({
    selector: 'qwyk-customer-users-list',
    templateUrl: './customer-users-list.component.html',
    styleUrls: ['./customer-users-list.component.scss'],
    providers: [DialogService],
})
export class CustomerUsersListComponent implements OnInit, OnDestroy {
    private md5Pipe = inject(MD5Pipe);
    private datePipe = inject(DatePipe);
    private modal = inject(DialogService);
    private urlEncodePipe = inject(UrlEncodePipe);
    private translate = inject(TranslateService);
    private localStorage = inject(LocalStorageService);
    private customerUsers = inject(CustomerUsersFacade);
    private portalTeamsFacade = inject(PortalTeamsFacade);
    private authentication = inject(AuthenticationFacade);

    public customers$ = this.portalTeamsFacade.allPortalTeams$;
    public loadingCustomers$ = this.portalTeamsFacade.loading$;
    public loading$ = this.customerUsers.loading$;
    public customerUsers$ = this.customerUsers.allCustomerUsers$.pipe(
        map(users =>
            users.map(user => ({
                ...user,
                deactivated_by: this.formatDeactivatedBy(user),
            }))
        )
    );
    public pagination$ = this.customerUsers.pagination$;
    public isMagayaNetwork$ = this.authentication.isMagayaNetwork$;

    public isTableView = true;
    public pageSize = 25;
    public selectAll = false;
    public searchForm = new FormGroup({
        name: new FormControl(null),
        email: new FormControl(null),
        team_id: new FormControl(null),
        tag: new FormControl(null),
        sort: new FormControl<string | string[]>('name'),
        sort_dir: new FormControl('asc'),
    });
    public columns: SelectItem[] = [
        {
            label: 'common.name',
            value: {
                field: 'name',
                header: 'common.name',
                default: true,
                filter: true,
                sort: true,
                sort_key: 'email',
            },
            disabled: true,
        },
        {
            label: 'common.email',
            value: {
                field: 'email',
                header: 'common.email',
                default: true,
                filter: true,
                sort: true,
                sort_key: 'email',
            },
        },
        {
            label: 'portal.registration.page1.label-username',
            value: {
                field: 'username',
                header: 'portal.registration.page1.label-username',
                default: true,
                filter: true,
                sort: false,
                sort_key: 'username',
            },
        },
        {
            label: 'common.customer',
            value: {
                field: 'team_name',
                header: 'common.customer',
                default: true,
                filter: true,
                sort: true,
                sort_key: 'customer_teams.name',
            },
        },
        {
            label: 'portal.shipment.search.placeholder.company',
            value: {
                field: 'company_name',
                header: 'portal.shipment.search.placeholder.company',
                default: false,
                filter: false,
                sort: true,
                sort_key: 'company_name',
            },
        },
        {
            label: 'portal.settings.profile.job-title',
            value: {
                field: 'job_title',
                header: 'portal.settings.profile.job-title',
                default: false,
                filter: false,
                sort: true,
                sort_key: 'job_title',
            },
        },
        {
            label: 'portal.cargo-items.columns.location',
            value: {
                field: 'location',
                header: 'portal.cargo-items.columns.location',
                default: false,
                filter: false,
                sort: true,
                sort_key: 'location',
            },
        },
        {
            label: 'common.country',
            value: {
                field: 'country.short_name',
                header: 'common.country',
                default: false,
                filter: false,
                sort: false,
                sort_key: null,
                nested: ['country', 'short_name'],
            },
        },
        {
            label: 'hub.shipment.sections.instructions.subsections.parties-fields.phone-number',
            value: {
                field: 'phone_number',
                header: 'hub.shipment.sections.instructions.subsections.parties-fields.phone-number',
                default: false,
                filter: false,
                sort: true,
                sort_key: 'phone_number',
            },
        },
        {
            label: 'common.sort-options.created-at',
            value: {
                field: 'created_at',
                header: 'common.sort-options.created-at',
                default: false,
                filter: false,
                sort: true,
                sort_key: 'created_at',
            },
        },
        {
            label: 'hub.administration.portal-users.last-login-at',
            value: {
                field: 'last_login_at',
                header: 'hub.administration.portal-users.last-login-at',
                default: false,
                filter: false,
                sort: true,
                sort_key: 'last_login_at',
            },
        },
        {
            label: 'hub.administration.portal-users.approved-by',
            value: {
                field: 'approved_by',
                header: 'hub.administration.portal-users.approved-by',
                default: false,
                filter: false,
                sort: false,
                sort_key: 'last_login_at',
            },
        },
        {
            label: 'hub.administration.portal-users.marketing-mails',
            value: {
                field: 'marketing_optin',
                header: 'hub.administration.portal-users.marketing-mails',
                default: false,
                filter: false,
                sort: false,
                sort_key: 'last_login_at',
            },
        },
        {
            label: 'common.active',
            value: {
                field: 'deactivated',
                header: 'common.active',
                default: true,
                filter: true,
                sort: true,
                sort_key: 'deactivated',
            },
        },
        {
            label: 'hub.administration.portal-users.deactivated-by',
            value: {
                field: 'deactivated_by',
                header: 'hub.administration.portal-users.deactivated-by',
                default: false,
                filter: false,
                sort: false,
                sort_key: null,
            },
        },
    ];
    public readonly VIEW_MODE = common.ViewMode;
    public readonly CustomerUserColumn = CustomerUserColumn;
    public userTagOptions = [
        {
            value: 'unverified',
            label: 'hub.home.email-verification-pending',
            icon: 'envelope',
            style: 'badge-outline-warning',
        },
        {
            value: 'unapproved',
            label: 'hub.administration.portal-users.approval-pending',
            icon: 'shield-check',
            style: 'badge-outline-danger',
        },
        {
            value: 'trashed',
            label: 'hub.administration.portal-users.deactivated-rejected',
            icon: 'ban',
            style: 'badge-danger',
        },
        {
            value: 'active',
            label: 'common.active',
            icon: 'check-circle',
            style: 'badge-outline-success',
        },
    ];

    private _selectedColumns = [];
    private _ref: DynamicDialogRef | undefined;

    /**
     * Filters (name, email, team_id, tag) actually applied on the last
     * search, as opposed to the (possibly unsubmitted) live form value.
     */
    private _appliedFilters: Record<string, unknown> = {};

    public get selectedTag(): string {
        return this.searchForm.get('tag')?.value;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public get selectedColumns(): any[] {
        return this._selectedColumns;
    }
    public set selectedColumns(value) {
        this._selectedColumns = value;
        this.saveTableCols();
    }

    public get computedColumns(): any[] {
        const base = [...this.selectedColumns];
        const deactivatedByCol = this.columns.find(
            col => col.value.field === CustomerUserColumn.DEACTIVATED_BY
        )?.value;

        if (this._appliedFilters['tag'] === 'trashed') {
            if (
                deactivatedByCol &&
                !base.find(
                    col => col.field === CustomerUserColumn.DEACTIVATED_BY
                )
            ) {
                return [...base, deactivatedByCol];
            }
        }
        return base;
    }

    ngOnInit(): void {
        this.translateColumns();
        this.restoreTableCols();
        this.portalTeamsFacade.loadPortalTeams({
            page: 1,
            limit: this.pageSize,
        });
    }

    public loadCustomerUsersLazy(event: TableLazyLoadEvent) {
        setTimeout(() => {
            let page = 1;

            this.pageSize =
                !event.rows || event.rows < 25 ? this.pageSize : event.rows;

            if (event.first && event.rows) {
                page = event.first / event.rows + 1;
            }
            if (event.sortField) {
                this.searchForm.patchValue(
                    {
                        sort: event.sortField || 'name',
                        sort_dir: event.sortOrder === 1 ? 'asc' : 'desc',
                    },
                    { emitEvent: false }
                );
            }

            this.fetch(page);
        }, 50);
    }

    public search(page = 1) {
        const { name, email, team_id, tag } = this.searchForm.value;
        this._appliedFilters = { name, email, team_id, tag };
        this.searchForm.markAsPristine();
        this.fetch(page);
    }

    public resetSearch() {
        this.searchForm.reset();
        this.search(1);
    }

    // Uses _appliedFilters (not the live form) so page/sort changes never apply unsearched filters.
    private fetch(page = 1) {
        const { sort, sort_dir } = this.searchForm.value;
        const query = {
            ...this._appliedFilters,
            sort,
            sort_dir,
            pageSize: this.pageSize,
            page,
        };
        Object.keys(query).forEach(key => {
            if (query[key] === null || query[key] === undefined) {
                delete query[key];
            }
        });

        this.customerUsers.loadCustomerUsers(query);
    }

    public onTableColReorder(e): void {
        const reorderedColumns = [...(e?.columns || [])];
        if (!reorderedColumns.length) {
            return;
        }

        const hadExplicitDeactivatedByColumn = this._selectedColumns.some(
            col => col.field === CustomerUserColumn.DEACTIVATED_BY
        );

        this._selectedColumns = reorderedColumns.filter(
            col =>
                col.field !== CustomerUserColumn.DEACTIVATED_BY ||
                hadExplicitDeactivatedByColumn
        );

        this.saveTableCols();
    }

    public onSelectAllChange(event: MultiSelectSelectAllChangeEvent) {
        this.selectAll = event.checked;
        if (event.checked) {
            this.selectedColumns = this.columns.map(col => col.value);
        } else {
            this.selectedColumns = this.columns
                .filter(col => col.value.default)
                .map(col => col.value);
        }
        this.saveTableCols();
    }

    public formatExport(value: { field: string; data: any }) {
        if (value.field === 'deactivated') {
            return value.data ? 'No' : 'Yes';
        }
        // Spreadsheet apps parse date-only values (e.g. "Sep 23, 2026") into
        // dates and re-render them in their own format. A leading zero-width
        // space keeps the cell as text so it matches the list.
        if (value.field === CustomerUserColumn.DEACTIVATED_BY && value.data) {
            return String.fromCharCode(0x200b) + value.data;
        }
        return value.data;
    }

    onImpersonateUser(user: Hub.CustomerUser) {
        this._ref = this.modal.open(CustomerUserImpersonateModalComponent, {
            position: 'center',
            showHeader: false,
            closable: false,
            width: '30%',
            contentStyle: {
                padding: 0,
            },
            data: {
                user,
            },
        });
    }

    public ngOnDestroy(): void {
        if (this._ref) {
            this._ref.close();
        }
    }

    public onSearchCustomers(term: string): void {
        this.portalTeamsFacade.loadPortalTeams({ name: term });
    }

    public onClearCustomersSearch(): void {
        this.portalTeamsFacade.loadPortalTeams({
            page: 1,
            limit: this.pageSize,
        });
    }

    public canOpenUser(
        user: Hub.CustomerUser,
        _isMagayaNetwork: boolean
    ): boolean {
        // Active portal users of Magaya-integrated organizations (e.g. Magaya
        // Supply Chain) can now be opened using the same form/layout as
        // non-integrated organizations. Deactivated users remain unselectable.
        return !user.deactivated;
    }

    public getFirstRecord(pagination?: common.PaginationMeta): number {
        if (!pagination) {
            return 0;
        }

        return pagination.per_page * (pagination.current_page - 1);
    }

    public getAvatarUrl(user: Hub.CustomerUser): string {
        return (
            'https://www.gravatar.com/avatar/' +
            this.md5Pipe.transform(user.email) +
            '?d=https%3A%2F%2Fui-avatars.com%2Fapi%2F/' +
            this.urlEncodePipe.transform(user.name) +
            '/64/097aff/ffffff/2//false/true/true'
        );
    }

    // Baked into the field itself so the CSV export (which only sees the
    // resolved field value, not the full row) includes the date too.
    // Older records may have deactivated_at without deactivated_by, so the
    // date must still be shown on its own.
    private formatDeactivatedBy(user: Hub.CustomerUser): string {
        const formattedDate = this.datePipe.transform(user.deactivated_at);

        return [user.deactivated_by, formattedDate].filter(Boolean).join(' - ');
    }

    private translateColumns(): void {
        this.columns.forEach(col => {
            col.label = this.translate.instant(col.label);
            col.value.header = this.translate.instant(col.value.header);
        });
    }

    /**
     * Saves table column layout to localstorage.
     * @private
     */
    private saveTableCols(): void {
        this.localStorage.setObject(
            'administration.portal-users.table-layout.v3',
            this._selectedColumns
        );
    }

    /**
     * Restores table column layout from localstorage.
     * @private
     */
    private restoreTableCols(): void {
        let columns = null;

        try {
            columns = this.localStorage.getObject(
                'administration.portal-users.table-layout.v3'
            );
        } catch {
            /* empty */
        }

        if (!columns) {
            columns = this.columns
                .filter(e => e.value.default)
                .map(e => e.value);
        }
        this._selectedColumns = columns;
    }
}
